import { localToday } from "../../../../utils";
import {
  ADJUSTMENT_DIRECTION_GROUP_LABEL,
  INTEREST_DIRECTION_GROUP_LABEL,
} from "../constants";
import { centsOf, settlementNetCentsOf } from "../settlementNote";
import {
  AccountCashAction,
  AccountCashCopy,
  AccountCashFormValues,
} from "./types";

export const FORM_ID = "account-cash";

export const ACTION_SEGMENTS: Array<{
  value: AccountCashAction;
  label: string;
}> = [
  { value: "accountFee", label: "Fee" },
  { value: "interest", label: "Interest" },
  { value: "adjustment", label: "Adjustment" },
];

/**
 * One keyed map for both modes, for the same reason `HOLDING_TRADE_COPY` is one:
 * the panel differs in wording far more than in structure.
 */
export const ACCOUNT_CASH_COPY: Record<AccountCashAction, AccountCashCopy> = {
  accountFee: {
    eyebrow: "RECORD ACCOUNT FEE",
    title: (investmentName) => `Record an account fee in ${investmentName}`,
    ariaLabel: "Record account fee form",
    submitLabel: "Record fee",
    compactActionLabel: "Save",
    compactTitle: "Record account fee",
    // The distinction that decides which panel the user should be in, said in
    // the one place they are about to get it wrong.
    amountHelper:
      "A custody, platform or inactivity charge on the whole account. A commission on one trade is a position fee instead.",
    discardTitle: "Cancel this account fee?",
  },
  interest: {
    eyebrow: "RECORD INTEREST",
    title: (investmentName) => `Record interest in ${investmentName}`,
    ariaLabel: "Record interest form",
    submitLabel: "Record interest",
    compactActionLabel: "Save",
    compactTitle: "Record interest",
    amountHelper:
      "The interest amount before any tax withheld, always as a positive number — Earned or Charged says which way it went.",
    taxHelper:
      "The tax the broker withheld at source on this interest. Leave it blank if none was withheld.",
    directionGroupLabel: INTEREST_DIRECTION_GROUP_LABEL,
    discardTitle: "Cancel this interest entry?",
  },
  adjustment: {
    eyebrow: "RECORD ADJUSTMENT",
    title: (investmentName) => `Record an adjustment in ${investmentName}`,
    ariaLabel: "Record adjustment form",
    submitLabel: "Record adjustment",
    compactActionLabel: "Save",
    compactTitle: "Record adjustment",
    // The distinction that decides which of the three segments the user wants,
    // said in the one place they are about to get it wrong.
    amountHelper:
      "A broker residual settled against the account's cash — a conversion rounding or a reversal. It is neither a fee nor interest.",
    directionGroupLabel: ADJUSTMENT_DIRECTION_GROUP_LABEL,
    discardTitle: "Cancel this adjustment?",
  },
};

/** A fresh date is required whenever the panel opens or resets. */
export const createDefaultAccountCash = (
  action: AccountCashAction,
): AccountCashFormValues => ({
  action,
  amountEur: "",
  date: localToday(),
  // Both directed actions are far more often earned than charged, and the field
  // is ignored entirely for a fee — so a default costs nothing and saves a tap.
  direction: "EARNED",
  taxWithheld: "",
  note: "",
});

// Charged interest is a cost the account paid, so there is no income for a tax to be withheld from.
export const showsInterestTax = ({
  action,
  direction,
}: Pick<AccountCashFormValues, "action" | "direction">): boolean =>
  action === "interest" && direction === "EARNED";

// Interest is refused on a linked broker, so it settles nothing.
export const accountCashNetCentsOf = ({
  action,
  amountEur,
  direction,
}: Pick<
  AccountCashFormValues,
  "action" | "amountEur" | "direction"
>): number => {
  if (action === "interest") {
    return 0;
  }
  const isCredit = action === "adjustment" && direction === "EARNED";
  return settlementNetCentsOf({
    amountCents: centsOf(amountEur),
    chargeCents: 0,
    direction: isCredit ? "IN" : "OUT",
  });
};
